import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Wordmark } from "@/components/landing/Wordmark";
import { ScreenplayPattern } from "@/components/landing/ScreenplayPattern";
import { LocalePicker } from "@/components/landing/LocalePicker";
import { appUrl } from "@/lib/referral";

const APP_SIGNUP = "https://work.voxiumscript.com";
const APP_SIGNIN = "https://work.voxiumscript.com";

export const Hero = () => {
  const handleSeeHow = () => {
    const target = document.getElementById("section-meet-you-there");
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative w-full min-h-screen bg-ink overflow-hidden"
      data-testid="hero-section"
    >
      {/* Screenplay pattern background */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <ScreenplayPattern className="w-full h-full" />
      </div>

      {/* Top nav row — wordmark left, sign-in right */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 pt-8">
        <a
          href="#hero-top"
          className="inline-flex items-center tap-extend"
          aria-label="Voxium Script home"
          data-testid="nav-wordmark-link"
        >
          <Wordmark tone="dark" size="nav" testId="nav-wordmark" />
        </a>
        <div className="flex items-center gap-6">
          <a
            href={appUrl(APP_SIGNIN)}
            className="btn-text btn-text-light tap-extend text-sm"
            data-testid="nav-signin-link"
          >
            Sign in
          </a>
          <LocalePicker current="en" />
        </div>
      </div>

      {/* Centered cream card */}
      <div
        id="hero-top"
        className="relative z-10 min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 sm:px-10 py-16 sm:py-20"
      >
        <div
          className="bg-cream max-w-prose3 w-full px-8 sm:px-14 py-12 sm:py-16 reveal is-visible shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]"
          data-testid="hero-card"
        >
          {/* Wordmark inside card (larger than nav) */}
          <div className="mb-10 sm:mb-12">
            <Wordmark tone="light" size="hero" testId="hero-wordmark" />
          </div>

          {/* Headline */}
          <h1
            className="display-serif text-nearblack text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] mb-6 sm:mb-8"
            data-testid="hero-headline"
          >
            Get your script ready for the green light.
          </h1>

          {/* Body */}
          <p
            className="font-body text-nearblack/85 text-base sm:text-lg leading-relaxed mb-10 sm:mb-12"
            data-testid="hero-body"
          >
            Every writer wants to be produced. Voxium Script gives you rigorous, honest
            coverage and story development — at a price serious writers can afford. So
            when you get the meeting, your script is ready.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center">
            <a
              href={appUrl(APP_SIGNUP)}
              className="btn-gold"
              data-testid="hero-primary-cta"
            >
              Start your first read
              <ArrowUpRight size={16} strokeWidth={2} />
            </a>
            <button
              type="button"
              onClick={handleSeeHow}
              className="btn-text tap-extend"
              data-testid="hero-secondary-cta"
            >
              See how it works
            </button>
          </div>

          {/* Hairline + family attribution */}
          <div className="mt-12 sm:mt-14 pt-6 border-t border-cream-200">
            <p
              className="font-body italic text-sm text-nearblack/70"
              data-testid="hero-family-attribution"
            >
              From the makers of{" "}
              <a
                href="https://voxiumapp.com"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-cream-200 underline-offset-4 hover:decoration-gold hover:text-gold-600 transition-colors"
              >
                Voxium
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
